import { FunctionComponent, useContext, useEffect, useState } from "react";
import { format, subDays } from "date-fns";
import { useLazyQuery } from "@apollo/client";
import { useAuth } from "../../context/AuthContext";
import SkeletonLoader from "../common/SkeletonLoader";
import { TASKS_BY_EMPLOYEE_ID } from "../../api/query";
import { useNavigate } from "react-router-dom";
import { classForStatus } from "../goals/GoalTaskView";

export type ActionRowType = {
    imageurl?: string;
    heading?: string;
};

const TaskActionRow: FunctionComponent<ActionRowType> = ({
    imageurl,
    heading,
}) => {
    const itemsPerPage = 3; // 3 tasks per page

    const navigate = useNavigate()

    const { userInfo,currentPageTask, setCurrentPageTask } = useAuth()
    const employeeID = userInfo?.employee_id || '';

    const now = new Date();
    const endDate = format(now, "yyyy-MM-dd'T'HH:mm:ssxxx").replace(/(\+|\-)\d{2}:\d{2}$/, "Z");

    const [getEmployeeTasks, { loading, data }] = useLazyQuery(
        TASKS_BY_EMPLOYEE_ID,
        {
            fetchPolicy: "no-cache"
        }
    );

    useEffect(() => {
        getEmployeeTasks({
            variables: {
                employee_id: parseInt(employeeID),
            }
        })
    }, [employeeID])

    if (loading) {
        return (
            <div
                className="self-stretch shadow-[0px_1px_4px_rgba(0,_0,_0,_0.25)] mobile-action-card-blg-dv rounded-lg bg-surface-bg-primary flex flex-col items-start justify-start p-4 box-border gap-[6px] max-w-full text-left text-base text-colors-text-black font-body-xs-semi-bold "
            >
                <div className="self-stretch flex flex-row items-center justify-start gap-[16px] max-w-full mq950:flex-wrap inner-mobile-action-card-blg-dv">
                    <div className="flex-1 flex flex-row items-start justify-start gap-[6px] max-w-full">
                        <h5 className="relative font-bold text-xl-dv mt-0 mb-[16px] capitalize dark:text-white">{heading}</h5>
                    </div>
                </div>
                <div className="w-full flex flex-col gap-4">
                    <div className="flex justify-between items-center">
                        <SkeletonLoader count={1} height="h-4" width="w-2/5" />
                        <SkeletonLoader count={1} height="h-6" width="w-20" borderRadius="rounded-full" />
                    </div>
                    <div className="flex gap-4 items-center">
                        <SkeletonLoader count={1} height="h-3" width="w-24" />
                        <SkeletonLoader count={1} height="h-3" width="w-28" />
                        <SkeletonLoader count={1} height="h-3" width="w-32" />
                    </div>
                    <div className="flex justify-between items-center">
                        <SkeletonLoader count={1} height="h-4" width="w-1/3" />
                        <SkeletonLoader count={1} height="h-6" width="w-20" borderRadius="rounded-full" />
                    </div>
                    <div className="flex gap-4 items-center">
                        <SkeletonLoader count={1} height="h-3" width="w-28" />
                        <SkeletonLoader count={1} height="h-3" width="w-24" />
                        <SkeletonLoader count={1} height="h-3" width="w-20" />
                    </div>
                    <div className="flex justify-between items-center">
                        <SkeletonLoader count={1} height="h-4" width="w-2/5" />
                        <SkeletonLoader count={1} height="h-6" width="w-20" borderRadius="rounded-full" />
                    </div>
                    <div className="flex gap-4 items-center">
                        <SkeletonLoader count={1} height="h-3" width="w-32" />
                        <SkeletonLoader count={1} height="h-3" width="w-28" />
                        <SkeletonLoader count={1} height="h-3" width="w-24" />
                    </div>
                </div>
            </div>
        )
    }

    const tasks = data?.tasks_by_employee_id?.tasks || [];

    const today = new Date();
    today.setHours(23, 59, 59, 999);

    const sortedTasks = tasks
        .filter((task: any) => new Date(task.end_datetime) <= today)
        .slice()
        .sort(
            (a: any, b: any) =>
                new Date(a.end_datetime).getTime() -
                new Date(b.end_datetime).getTime()
        );

    // Pagination calculations
    const totalPages = Math.ceil(sortedTasks.length / itemsPerPage);
    const startIndex = currentPageTask * itemsPerPage;
    const visibleTasks = sortedTasks.slice(startIndex, startIndex + itemsPerPage);

    return (
        <div
            className="self-stretch shadow-[0px_1px_4px_rgba(0,_0,_0,_0.25)] mobile-action-card-blg-dv rounded-lg bg-surface-bg-primary flex flex-col items-start justify-start p-4 box-border gap-[6px] max-w-full text-left text-base text-colors-text-black font-body-xs-semi-bold "
        >
            <div className="self-stretch flex flex-row items-center justify-start gap-[16px] max-w-full mq950:flex-wrap inner-mobile-action-card-blg-dv">
                <div className="flex-1 flex flex-row items-start justify-start gap-[6px] max-w-full">

                    <h5 className="relative font-bold text-xl-dv  mt-0 mb-[16px] capitalize dark:text-white">{`${heading} (${sortedTasks?.length ?? 0})`}</h5>
                </div>
                  <div className="flex items-center gap-2 cursor-pointer"  onClick={() => navigate('/employee_task_list')}>
                    <span className="text-sm text-gray-600 dark:text-gray-300">View Tasks</span>
                    <svg className="w-5 h-5 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </div>
            </div>
            <div className={`max-w-full flex min-w-full max-h-[284px] overflow-x-hidden overflow-y-auto ${data?.tasks_by_employee_id?.tasks?.length > 6 ? 'shiftwith-exception-scroll' : ''}`}>
                <div className="grid grid-cols-3 mq1900:grid-cols-3 mq1425:grid-cols-2 mq1275:grid-cols-2 mq950:grid-cols-2 mq825:grid-cols-1 mq575:grid-cols-1 min-w-full  gap-[24px]">
                    {visibleTasks.length > 0 && visibleTasks.map((task: any) => {
                        return (
                            <div className="customtask_card_ui_dv  w-full relative rounded cursor-pointer bg-white border-whitesmoke border-solid border-[1px] box-border  shrink-0 grid flex-col items-start justify-start pt-4 p-[16px] text-left text-base text-gray font-manrope"
                                key={task?.id}
                                onClick={() => {
                                    navigate("/task_detail", {
                                        state: {
                                            taskId: task?.id,
                                        }
                                    })
                                }}
                            >
                                <div className="w-full relative rounded cursor-pointer   shrink-0 flex flex-col items-start justify-center text-left text-base text-gray font-manrope">
                                    <div className="self-stretch flex flex-col items-start justify-start">
                                        <div className="self-stretch flex flex-row items-center justify-start">
                                            <div className="flex-1 flex flex-row items-center justify-start pb-2">
                                                <p className="relative font-semibold text-sm my-0 capitalize tasktitle_card">{task?.title}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid gap-2 w-full">
                                        <hr className="bdr-custom-taskcrad" />
                                        <div className="self-stretch flex flex-col items-start justify-center gap-2 text-dimgray">
                                            <div className="self-stretch flex flex-row items-start justify-start">
                                                <p className="flex-1 relative dark:text-white my-0">{task?.static_task_type_name}</p>
                                            </div>
                                            <hr className="bdr-custom-taskcrad" />
                                            <div className=" taskcard_inner_box self-stretch flex flex-col items-start justify-start gap-2 text-dimgray">
                                                <div className="self-stretch flex flex-row items-start justify-start gap-2">
                                                    <p className="w-[120px] relative inline-block shrink-0 dark:text-white my-0">Goal</p>
                                                    <p className="relative font-semibold text-gray dark:text-white my-0 tasktitle_card">{task?.goal_name}</p>
                                                </div>
                                                <div className="self-stretch flex flex-row items-start justify-start gap-2">
                                                    <p className="w-[120px] relative inline-block shrink-0 dark:text-white my-0">Due Date</p>
                                                    <p className="relative font-semibold text-gray dark:text-white my-0">{format(task?.end_datetime, 'MM-dd-yyyy')}</p>
                                                </div>
                                                <div className="self-stretch flex flex-row items-start justify-start gap-2">
                                                    <p className="w-[120px] relative inline-block shrink-0 dark:text-white my-0">Assigned By</p>
                                                    <p className="relative font-semibold text-gray dark:text-white my-0">{task?.created_by_name}</p>
                                                </div>
                                                {task?.dependency_task_id && task?.dependency_task_id !== null && (
                                                    <div className="self-stretch flex flex-row items-start justify-start gap-2">
                                                        <p className="w-[120px] relative inline-block shrink-0 dark:text-white my-0">Parent Task</p>
                                                        <p className="relative font-semibold text-gray dark:text-white my-0">{task?.dependency_task_name}</p>
                                                    </div>
                                                )}
                                            </div>

                                        </div>
                                        {/* <hr className="bdr-custom-taskcrad" /> */}
                                        <div className="self-stretch flex flex-row items-start justify-between gap-0">
                                            <div className={`rounded-[50px] bg-antiquewhite border-orange border-solid border-[1px] flex flex-row items-center justify-center py-1 px-4 ${classForStatus(task?.status_name)}`}>
                                                <p className="relative dark:text-white my-0">{task?.status_name}</p>
                                            </div>
                                            {task?.task_tags.length > 0 && (
                                                task?.task_tags?.map((t: any) => {
                                                    return (
                                                        <div className="rounded-[100px] bg-crimson right_status_clockout flex flex-row items-center justify-center py-1 px-2 text-sm text-white">
                                                            <div className="relative font-semibold">{t?.tags_name}</div>
                                                        </div>
                                                    )

                                                })
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                    {visibleTasks && visibleTasks.length === 0 && (
                        <>No Task Available</>
                    )}
                    {
                        data?.get_missed_clockin_clockout?.status === "Failure" && (
                            <div className="border-[1px] border-solid border-surface-border min-w-full bg-white flex justify-center items-center action-nodeta_card">
                                <span className="text-left text-sm text-text-text-secondary"> No data available</span>
                            </div>
                        )
                    }
                </div>
            </div>
            {/* Pagination dots */}
            {totalPages > 1 && (
                <div className="flex justify-center items-center mt-4 gap-2 pagination-action-blg">
                    {Array.from({ length: totalPages }).map((_, index) => (
                        <button
                            key={index}
                            onClick={() => setCurrentPageTask(index)}
                            className={`h-3 w-3 rounded-full transition-all ${index === currentPageTask ? " scale-110 active-pagination" : "bg-gray-300 hover:bg-gray-400 unactive-pagination"
                                }`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default TaskActionRow;
